import { enhanceError, isError } from '@allurion/utils';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';

import { createProviderPassword } from 'src/api/UserApi';
import { Footer } from 'src/components/Footer/Footer';
import { Seo } from 'src/components/Seo';
import { Loader } from 'src/components/ui/Loader';
import { CreatePasswordForm } from 'src/forms/CreatePassword/CreatePasswordForm';
import { useAppNavigate } from 'src/hooks/useAppNavigate';
import LogoImgSrc from 'src/images/wordmark-white.svg';
import globalMessages from 'src/messages/global.messages';
import { Logger } from 'src/services/Logger';

import { FullscreenContainer } from './shared-page-elements';

import styles from './CreatePasswordPage.module.scss';

export default function CreatePasswordPage() {
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { username, confirmationCode } = useParams();
  const { toLoginPage } = useAppNavigate();

  async function createPassword(newPassword: string) {
    try {
      setIsLoading(true);
      await createProviderPassword(username!, newPassword, confirmationCode!);
      setIsLoading(false);
      toLoginPage(false);
    } catch (error) {
      if (isError(error)) {
        switch (error.message) {
          case 'user not found':
            setErrorMessage(intl.formatMessage(globalMessages.UserNotFoundException));
            break;

          case 'Confirmation code expired':
            setErrorMessage(intl.formatMessage(globalMessages.ExpiredCodeException));
            break;

          case 'User already confirmed':
            setErrorMessage(intl.formatMessage(globalMessages.UserAlreadyConfirmed));
            break;

          default:
            setErrorMessage(intl.formatMessage(globalMessages.UnknownException));
            Logger.captureException(enhanceError(error, { prefix: 'Failed to create password' }));
            break;
        }
      } else {
        setErrorMessage(intl.formatMessage(globalMessages.UnknownException));
      }

      setIsLoading(false);
    }
  }

  return (
    <FullscreenContainer>
      <Seo title="Create Password" />
      {errorMessage ? (
        <div className={styles.content} data-sentry-unmask>
          <Link to="/" className={styles.logoTitleWrapperLink}>
            <img src={LogoImgSrc} alt="Allurion logo" className={styles.logo} />
          </Link>
          <p className={styles.text}>{errorMessage}</p>
        </div>
      ) : (
        <div className={styles.content}>
          <Link to="/manage-users" className={styles.logoTitleWrapperLink}>
            <img src={LogoImgSrc} alt="Allurion logo" className={styles.logo} />
          </Link>
          <p className={styles.text}>{intl.formatMessage(globalMessages.passwordSetup)}</p>
          <CreatePasswordForm
            initialValues={{
              password: '',
              confirmPassword: '',
            }}
            onSubmit={({ password }) => {
              createPassword(password);
            }}
          />
        </div>
      )}

      <Footer />
      <Loader isLoading={isLoading} />
    </FullscreenContainer>
  );
}
